import { useState } from "react";
import styled from "styled-components";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import {
  Trip,
  PlanningInfo,
  TransactionAndAuthorizationOnTrip,
} from "./DayTripDetails";
import { TripOrPlanningDetails } from "./TripOrPlanningDetails";
import { colors } from "../../../styles/figmaColors";
import { LittleCardAsset } from "../../../assets/assets";

const minutesSize = 0.1;

export interface HoursBarTripsProps {
  trips: Trip[];
  driverName: string | null;
  plannings: PlanningInfo[];
  transactionsToDisplay: TransactionAndAuthorizationOnTrip[];
}

const getHoursAndMinutesFromMinutes = (minutes: number): string => {
  const hours = minutes > 59 ? Math.floor(minutes / 60) : 0;
  const minutesLeft = hours ? minutes - hours * 60 : minutes;
  return `${hours}:${minutesLeft < 10 ? "0" + minutesLeft : minutesLeft}`;
};
const isOnTwoDays = (startDate: string, endDate: string): boolean => {
  return new Date(startDate).getDay() != new Date(endDate).getDay();
};

const getDurationInMinutes = (
  startDate: string,
  endDate: string,
  isOnTwoDays: boolean,
): number => {
  return (
    (new Date(endDate).getHours() - new Date(startDate).getHours()) * 60 +
    new Date(endDate).getMinutes() -
    new Date(startDate).getMinutes() +
    (isOnTwoDays ? 24 * 60 : 0)
  );
};

export const HoursBarTrips = (props: HoursBarTripsProps) => {
  const [tripOverview, setTripOverview] = useState<number | null>(null);
  const [planningOverview, setPlanningOverview] = useState<number | null>(null);
  const [currentTimeOverview, setCurrentTimeOverview] = useState<number | null>(
    null,
  );
  const [hoursBarScrollPosition, setHoursBarScrollPosition] =
    useState<number>(0);

  const hoursBarContainerWidth =
    document.querySelector(".hours-bar-container")?.clientWidth ??
    window.innerWidth - 100;

  //DATA FOR DISPLAYING THE TRIP DETAILS
  const currentTrip = tripOverview !== null ? props.trips[tripOverview] : null;
  const isCurrentTripOnTwoDays = !!(
    currentTrip && isOnTwoDays(currentTrip.startDate, currentTrip.endDate)
  );

  const currentTripDurationInMinutes = currentTrip
    ? getDurationInMinutes(
        currentTrip.startDate,
        currentTrip.endDate,
        isCurrentTripOnTwoDays,
      )
    : 0;

  const tripData = currentTrip && {
    ...currentTrip,
    driver: props.driverName,
    duration: Number(currentTripDurationInMinutes.toFixed(0)),
  };

  //DATA FOR DISPLAYING THE PLANNING DETAILS
  const currentPlanning =
    planningOverview !== null ? props.plannings[planningOverview] : null;
  const isCurrentPlanningOnTwoDays = !!(
    currentPlanning &&
    isOnTwoDays(currentPlanning.startTime, currentPlanning.endTime)
  );

  const currentDurationInMinutes = currentPlanning
    ? getDurationInMinutes(
        currentPlanning.startTime,
        currentPlanning.endTime,
        isCurrentPlanningOnTwoDays,
      )
    : 0;
  const planningData = currentPlanning && {
    ...currentPlanning,
    driver: props.driverName,
    duration: Number(currentDurationInMinutes.toFixed(0)),
  };

  //HANDLE IF THE LAST DATE ENDS ON ANOTHER DAY
  const lastTrip = props.trips[props.trips.length - 1];
  const lastPlanning = props.plannings[props.plannings.length - 1];

  const isLastTripOnAnotherDay =
    lastTrip && isOnTwoDays(lastTrip.startDate, lastTrip.endDate);

  const isLastPlanningOnAnotherDay =
    lastPlanning && isOnTwoDays(lastPlanning.startTime, lastPlanning.endTime);

  const lastEndDate =
    isLastTripOnAnotherDay ||
    isLastPlanningOnAnotherDay ||
    !lastPlanning?.endTime
      ? !lastPlanning?.endTime || lastTrip.endDate > lastPlanning?.endTime
        ? lastTrip.endDate
        : lastPlanning.endTime
      : null;

  const lastEndDateInMinutes =
    lastEndDate &&
    new Date(lastEndDate).getHours() * 60 +
      new Date(lastEndDate).getMinutes() +
      10;

  //GET MINUTES TO DISPLAYED
  const minutesInADay = Array.from({ length: 24 * 60 }, (_, m) => m);
  const minutesExceededOnAnotherDay = lastEndDateInMinutes
    ? Array.from({ length: 24 * 60 }, (_, m) => {
        if (m < lastEndDateInMinutes) {
          return m + 24 * 60;
        }
        return undefined;
      }).filter((m) => m !== undefined)
    : [];

  const minutesToDisplayIndex = [
    ...minutesInADay,
    ...minutesExceededOnAnotherDay,
  ];

  return (
    <StyledAllWidth>
      <StyledHoursBarContainer className="hours-bar-container">
        <StyledHoursBarContainerOverflow
          onScroll={(e) => {
            const leftScrollPosition =
              (e.target as HTMLDivElement)?.scrollLeft ?? 0;
            setHoursBarScrollPosition(leftScrollPosition);
          }}
        >
          {tripData && (
            <StyledFlex>
              <TripOrPlanningDetails
                dataDisplay={tripData}
                type={"TRIP"}
                scrollPosition={hoursBarScrollPosition}
                hoursBarContainerWidth={hoursBarContainerWidth}
                hasAttachedPlanning={props.plannings.length > 0}
                minutesSize={minutesSize}
              />
            </StyledFlex>
          )}
          {planningData && (
            <TripOrPlanningDetails
              type={"PLANNING"}
              scrollPosition={hoursBarScrollPosition}
              hoursBarContainerWidth={hoursBarContainerWidth}
              hasAttachedPlanning={props.plannings.length > 0}
              dataDisplay={{
                driver: planningData.driver,
                duration: planningData.duration,
                startDate: planningData.startTime,
                endDate: planningData.endTime,
                distance: Number(planningData.expectedMileage.toFixed(2)),
                harshAccelerationsCount: null,
                harshBrakesCount: null,
                sharpTurnsCount: null,
                clientName: planningData.clientName,
              }}
              minutesSize={minutesSize}
            />
          )}
          <StyledColumn>
            <StyledHoursBar>
              <Spacer x={2} />
              {minutesToDisplayIndex.map((index) => {
                const minutes = index < 24 * 60 ? index : index - 24 * 60;
                return (
                  <StyledHour
                    style={{
                      minWidth: `${minutesSize}rem`,
                      maxWidth: `${minutesSize}rem`,
                      height: "100%",
                    }}
                    key={index}
                    onMouseEnter={() => {
                      setCurrentTimeOverview(minutes);
                    }}
                    onMouseLeave={() => {
                      setCurrentTimeOverview(null);
                    }}
                  >
                    {currentTimeOverview && currentTimeOverview === minutes && (
                      <StyledAbsoluteHourOverview>
                        <StyledLineGreen />
                        <Spacer y={0.25} />
                        <StyledFlex>
                          <Spacer x={0.5} />
                          {getHoursAndMinutesFromMinutes(currentTimeOverview)}
                          <Spacer x={0.5} />
                        </StyledFlex>
                        <Spacer y={0.25} />
                      </StyledAbsoluteHourOverview>
                    )}
                    {minutes % 60 === 0 && <>{minutes / 60 + " H"}</>}
                  </StyledHour>
                );
              })}
              <Spacer x={3} />
            </StyledHoursBar>
            <Spacer y={1} />
            <StyledHoursBarDetails>
              <TripsOnHoursBar
                trips={props.trips}
                setTripOverview={setTripOverview}
              />
              {props.transactionsToDisplay.length > 0 && (
                <StyledAbsoluteTransaction
                  $hasPlanning={props.plannings.length > 0}
                >
                  <TransactionsOnHoursBar
                    transactionsToDisplay={props.transactionsToDisplay}
                  />
                </StyledAbsoluteTransaction>
              )}
              {props.plannings.length > 0 && (
                <PlanningsOnHoursBar
                  plannings={props.plannings}
                  setPlanningOverview={setPlanningOverview}
                />
              )}
            </StyledHoursBarDetails>
          </StyledColumn>
        </StyledHoursBarContainerOverflow>
      </StyledHoursBarContainer>
    </StyledAllWidth>
  );
};

const StyledLineGreen = styled.div`
  position: absolute;
  height: 30rem;
  border-right: 0.2rem solid #00ffa3;
`;

const TripsOnHoursBar = (props: {
  trips: Trip[];
  setTripOverview: (index: number | null) => void;
}) => {
  return (
    <StyledDisplayOnHoursBar>
      <Spacer x={2} />
      {props.trips.map((t, index) => {
        const isTripsOnTwoDays = isOnTwoDays(t.startDate, t.endDate);
        const durationInMinutes = getDurationInMinutes(
          t.startDate,
          t.endDate,
          isTripsOnTwoDays,
        );

        const previousTrip = props.trips[index - 1];
        const gapTimeWithPreviousTripInMinutes = previousTrip
          ? (new Date(t.startDate).getTime() -
              new Date(previousTrip.endDate).getTime()) /
            60000
          : new Date(t.startDate).getHours() * 60 +
            new Date(t.startDate).getMinutes();

        return (
          <StyledFlex key={"trip" + index}>
            <Spacer x={gapTimeWithPreviousTripInMinutes * minutesSize} />
            <StyledPeriod
              $width={durationInMinutes * minutesSize}
              onMouseEnter={() => props.setTripOverview(index)}
              onMouseLeave={() => props.setTripOverview(null)}
              $color={"#00FFA3"}
            />
          </StyledFlex>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const TransactionsOnHoursBar = (props: {
  transactionsToDisplay: TransactionAndAuthorizationOnTrip[];
}) => {
  return (
    <StyledDisplayOnHoursBar>
      <Spacer x={2} />
      {props.transactionsToDisplay.map((t, index) => {
        const cardSizeInMinutes = 14;
        const previousTransaction = props.transactionsToDisplay[index - 1];

        const gapTimeWithPreviousTransactionInMinutes = previousTransaction
          ? (new Date(t.date).getTime() -
              new Date(previousTransaction.date).getTime()) /
              60000 -
            cardSizeInMinutes
          : new Date(t.date).getHours() * 60 + new Date(t.date).getMinutes();

        return (
          <StyledCardAsset key={"transaction" + index}>
            <Spacer x={gapTimeWithPreviousTransactionInMinutes * minutesSize} />
            <LittleCardAsset />
          </StyledCardAsset>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const PlanningsOnHoursBar = (props: {
  plannings: PlanningInfo[];
  setPlanningOverview: (index: number | null) => void;
}) => {
  return (
    <StyledDisplayOnHoursBar>
      <Spacer x={2} />
      {props.plannings.map((p, index) => {
        const isPlanningOnTwoDays = isOnTwoDays(p.startTime, p.endTime);
        const durationInMinutes = getDurationInMinutes(
          p.startTime,
          p.endTime,
          isPlanningOnTwoDays,
        );

        const previousPlanning = props.plannings[index - 1];
        const gapTimeWithPreviousTripInMinutes = previousPlanning
          ? (new Date(p.startTime).getTime() -
              new Date(previousPlanning.endTime).getTime()) /
            60000
          : new Date(p.startTime).getHours() * 60 +
            new Date(p.startTime).getMinutes();

        return (
          <StyledFlex key={"planning" + index}>
            <Spacer x={gapTimeWithPreviousTripInMinutes * minutesSize} />
            <StyledPeriod
              $width={durationInMinutes * minutesSize}
              onMouseEnter={() => props.setPlanningOverview(index)}
              onMouseLeave={() => props.setPlanningOverview(null)}
              $color={"#D1E9FF"}
            />
          </StyledFlex>
        );
      })}
    </StyledDisplayOnHoursBar>
  );
};

const StyledAbsoluteHourOverview = styled.div`
  position: absolute;
  background-color: ${"#00FFA3"};
  color: ${colors["colors/text/darkGrey"]};
  ${typographies["Body/XXS"]};
  border-radius: 1rem;
  z-index: 100;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const StyledHoursBar = styled.div`
  display: flex;
  color: ${colors["colors/text/lightGrey"]};
  ${typographies["Body/XXS"]};
  height: 2rem;
  align-items: flex-start;
  width: 100%;
  border-bottom: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
`;

const StyledDisplayOnHoursBar = styled.div`
  display: flex;
  height: 5.5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledAbsoluteTransaction = styled.div<{ $hasPlanning: boolean }>`
  position: absolute;
  height: ${({ $hasPlanning }) => ($hasPlanning ? "50%" : "100%")};
  pointer-events: none;
`;

const StyledHoursBarDetails = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;
  height: 100%;
`;

const StyledHour = styled.div`
  position: relative;
  display: flex;
  min-width: ${minutesSize}srem;
  height: 100%;
  align-items: center;
  white-space: nowrap;
`;

const StyledPeriod = styled.div<{ $width: number; $color: string }>`
  min-width: ${({ $width }) => $width}rem;
  height: 4.5rem;
  border-radius: 0.5rem;
  background-color: ${({ $color }) => $color};
  display: flex;
  align-items: flex-start;
`;

const StyledHoursBarContainerOverflow = styled.div`
  display: flex;
  overflow: auto;
  width: 100%;
`;

const StyledHoursBarContainer = styled.div`
  display: flex;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
`;

const StyledAllWidth = styled.div`
  width: 100%;
  display: flex;
  overflow: hidden;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFlex = styled.div`
  display: flex;
`;

export const StyledCardAsset = styled.div`
  display: flex;
  & svg {
    width: 1.4rem;
    height: 1.0625rem;
  }
`;
